import type { ConversationAttributeItem } from '@/entities/attributes';
import type { ConversationAttrValueInput, ConversationClientInput } from '@/entities/conversations';
import {
  type AttributeValueDraft,
  createEmptyAttributeValue,
  getAttributeValueKind,
  toAttributeInputValue,
} from '@/features/attribute-filters';
import type { TimeIntervalFormat } from '@/shared/lib';

/** Свойство набора значений «сотрудник» */
export const EMPLOYEE_PROPERTY = 'employee';

/**
 * Свойство набора значений «клиент»: части имени ({@link CLIENT_NAME_PARTS}) вводятся текстом, каждая в своё
 * поле, а не выбираются из клиентов системы
 */
export const CLIENT_PROPERTY = 'client';

/** Части имени клиента в порядке их полей */
export const CLIENT_NAME_PARTS = ['lastName', 'firstName', 'middleName'] as const;

/** Имя клиента в том виде, в каком его держат поля ввода: по строке на каждую часть */
type ClientNameDraft = Record<(typeof CLIENT_NAME_PARTS)[number], string>;

/** Значение строки набора: имя клиента — по частям, значение любого другого свойства — одним полем */
export type RecordPropertyValue = AttributeValueDraft | ClientNameDraft;

/** Свойство набора значений «время звонка» */
export const CALL_TIME_PROPERTY = 'callTime';

/** Начало ключа свойства-атрибута: путь атрибута за ним не совпадёт с ключом поля записи */
const ATTRIBUTE_PROPERTY_PREFIX = 'attribute:';

/** Строка набора значений: свойство записи и заданное ему значение */
export type RecordPropertyEntry = {
  /**
   * Ключ свойства: {@link EMPLOYEE_PROPERTY}, {@link CLIENT_PROPERTY}, {@link CALL_TIME_PROPERTY} либо ключ
   * атрибута ({@link toAttributeProperty})
   */
  property: string;
  /** Значение в том виде, в каком его держат поля ввода */
  value: RecordPropertyValue;
};

/** Форма набора значений блока загрузки */
export type RecordValueSetForm = {
  /** Свойства набора в порядке добавления */
  properties: RecordPropertyEntry[];
};

/** Имя поля значения строки набора в форме */
export type RecordPropertyValueName = `properties.${number}.value`;

/** Набор значений в формате запросов создания записи и проставления значений отмеченным записям */
export type RecordValues = {
  /** Сотрудник записи */
  employeeId?: number;
  /** Клиент записи — только заполненные части имени, без пробелов по краям */
  client?: ConversationClientInput;
  /** Время звонка, ISO 8601 */
  startedAt?: string;
  /** Значения атрибутов */
  attributes: ConversationAttrValueInput[];
};

/** Ключ свойства набора для атрибута */
export const toAttributeProperty = (path: string): string => `${ATTRIBUTE_PROPERTY_PREFIX}${path}`;

/** Атрибут свойства набора; `undefined` — свойство не атрибут либо атрибута нет среди доступных набору */
export const getPropertyAttribute = (
  property: string,
  attributesByPath: ReadonlyMap<string, ConversationAttributeItem>,
): ConversationAttributeItem | undefined =>
  property.startsWith(ATTRIBUTE_PROPERTY_PREFIX)
    ? attributesByPath.get(property.slice(ATTRIBUTE_PROPERTY_PREFIX.length))
    : undefined;

/** Пустое значение нового свойства набора — под поле, которым оно вводится */
export const createEmptyPropertyValue = (
  property: string,
  attributesByPath: ReadonlyMap<string, ConversationAttributeItem>,
  timeDisplayFormat: TimeIntervalFormat,
): RecordPropertyValue => {
  if (property === CLIENT_PROPERTY) return { lastName: '', firstName: '', middleName: '' };
  const attribute = getPropertyAttribute(property, attributesByPath);
  const kind = attribute ? getAttributeValueKind(attribute, timeDisplayFormat) : null;
  return kind ? createEmptyAttributeValue(kind) : '';
};

/** Значение строки набора — имя клиента по частям */
const isClientNameDraft = (value: RecordPropertyValue): value is ClientNameDraft =>
  typeof value === 'object' && !Array.isArray(value);

/** Заполненные части имени клиента без пробелов по краям; `null` — не заполнена ни одна часть */
const toClientInput = (value: RecordPropertyValue): ConversationClientInput | null => {
  if (!isClientNameDraft(value)) return null;
  const client: ConversationClientInput = {};
  for (const part of CLIENT_NAME_PARTS) {
    // Часть из одних пробелов заполненной не считается
    const text = value[part].trim();
    if (text !== '') client[part] = text;
  }
  return Object.keys(client).length > 0 ? client : null;
};

/**
 * Набор значений в формате запросов; `null` — у какого-то свойства значение не задано или не
 * читается по виду поля, и отправлять набор нельзя.
 */
export const buildRecordValues = (
  entries: RecordPropertyEntry[],
  attributesByPath: ReadonlyMap<string, ConversationAttributeItem>,
  timeDisplayFormat: TimeIntervalFormat,
): RecordValues | null => {
  const values: RecordValues = { attributes: [] };

  for (const { property, value } of entries) {
    if (property === EMPLOYEE_PROPERTY) {
      if (typeof value !== 'number') return null;
      values.employeeId = value;
      continue;
    }
    if (property === CLIENT_PROPERTY) {
      const client = toClientInput(value);
      if (!client) return null;
      values.client = client;
      continue;
    }
    if (property === CALL_TIME_PROPERTY) {
      if (typeof value !== 'string' || value === '') return null;
      values.startedAt = value;
      continue;
    }

    const attribute = getPropertyAttribute(property, attributesByPath);
    const kind = attribute ? getAttributeValueKind(attribute, timeDisplayFormat) : null;
    const inputValue = kind && !isClientNameDraft(value) ? toAttributeInputValue(kind, value) : undefined;
    if (!attribute || inputValue === undefined) return null;
    values.attributes.push({ path: attribute.path, value: inputValue });
  }

  return values;
};
